// ⚠️ PERFORM CORE — Ne pas modifier sans validation complète
// Connecté à : profiles
// Utilisé dans : AppSidebar, Dashboard, ProfilePage, OnboardingQuiz
import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { toastError } from "@/lib/errorToast";
import { emitProfileChange } from "@/lib/profileChangeEmitter";

export interface ProfileData {
  // From profiles table
  display_name: string | null;
  pseudo: string | null;
  privacy_status: string;
  onboarding_completed: boolean;
  onboarding_step: number;
  sport_level: string | null;
  // Health data (from edge function, NOT from profiles)
  age: number | null;
  sex: string | null;
  weight_kg: number | null;
  height_cm: number | null;
}

const defaultProfile: ProfileData = {
  display_name: null,
  pseudo: null,
  privacy_status: "anonymous",
  onboarding_completed: false,
  onboarding_step: 0,
  sport_level: null,
  age: null,
  sex: null,
  weight_kg: null,
  height_cm: null,
};

/** Stable query key — also invalidated by useProfileChangeInvalidation */
export const PROFILE_QUERY_KEY = (userId: string) => ["profile", userId];

async function fetchProfile(userId: string): Promise<ProfileData> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);

  let health: { weight_kg: number | null; height_cm: number | null; age: number | null; sex: string | null } | null = null;

  const [profileRes, healthResult] = await Promise.all([
    supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase.functions
      .invoke("health-data", { method: "GET" })
      .then((res) => {
        clearTimeout(timeout);
        return res;
      })
      .catch((err) => {
        console.warn("[useProfile] health-data timeout or error, degrading gracefully", err);
        return { data: null, error: err };
      }),
  ]);

  clearTimeout(timeout);

  health = (healthResult.data as typeof health) ?? null;

  const data = profileRes.data;
  if (!data) return defaultProfile;

  return {
    display_name: data.display_name,
    pseudo: (data as any).pseudo || null,
    privacy_status: (data as any).privacy_status || "anonymous",
    onboarding_completed: (data as any).onboarding_completed ?? false,
    onboarding_step: (data as any).onboarding_step ?? 0,
    sport_level: data.sport_level,
    age: health?.age ?? null,
    sex: health?.sex ?? null,
    weight_kg: health?.weight_kg ?? null,
    height_cm: health?.height_cm ?? null,
  };
}

export function useProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const { data: profile = defaultProfile, isLoading: loading } = useQuery({
    queryKey: userId ? PROFILE_QUERY_KEY(userId) : ["profile", "anon"],
    queryFn: () => fetchProfile(userId!),
    enabled: !!userId,
    staleTime: 30_000, // 30s — re-reads after invalidation anyway
  });

  const mutation = useMutation({
    mutationFn: async ({ patch, propagate }: { patch: Partial<ProfileData>; propagate: boolean }) => {
      if (!userId) throw new Error("No user");

      // Separate health fields from profile fields
      const healthFields = ["weight_kg", "height_cm", "age", "sex"] as const;
      const healthPatch: Record<string, unknown> = {};
      const profilePatch: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(patch)) {
        if ((healthFields as readonly string[]).includes(key)) {
          healthPatch[key] = value;
        } else {
          profilePatch[key] = value;
        }
      }

      // Update profile fields in profiles table
      if (Object.keys(profilePatch).length > 0) {
        const { error } = await supabase
          .from("profiles")
          .upsert({ user_id: userId, ...profilePatch } as any, { onConflict: "user_id" });
        if (error) throw error;
      }

      // Update health fields via encrypted edge function
      if (Object.keys(healthPatch).length > 0) {
        const currentHealth = {
          weight_kg: profile.weight_kg,
          height_cm: profile.height_cm,
          age: profile.age,
          sex: profile.sex,
          ...healthPatch,
        };
        const { error } = await supabase.functions.invoke("health-data", {
          method: "POST",
          body: currentHealth,
        });
        if (error) throw error;
      }

      return { patch, propagate };
    },
    onSuccess: ({ patch, propagate }) => {
      // Optimistic local cache update
      if (userId) {
        queryClient.setQueryData(PROFILE_QUERY_KEY(userId), (old: ProfileData | undefined) => ({
          ...(old ?? defaultProfile),
          ...patch,
        }));
      }
      // Only trigger professional propagation when requested
      if (propagate) {
        emitProfileChange();
      }
    },
    onError: (error: any) => {
      toastError("Erreur de sauvegarde", "Sauvegarder le profil", error.message ?? String(error));
      console.error("[useProfile] mutation error", error);
    },
  });

  /**
   * Update profile with propagation control.
   * @param patch — fields to update
   * @param propagate — if true, triggers professional system sync (default: true)
   */
  const updateProfile = useCallback(
    async (patch: Partial<ProfileData>, propagate = true) => {
      await mutation.mutateAsync({ patch, propagate });
    },
    [mutation]
  );

  // setProfile kept for rare imperative overrides (e.g. onboarding flow)
  const setProfile = useCallback(
    (updater: ProfileData | ((prev: ProfileData) => ProfileData)) => {
      if (!userId) return;
      queryClient.setQueryData(PROFILE_QUERY_KEY(userId), (old: ProfileData | undefined) => {
        const prev = old ?? defaultProfile;
        return typeof updater === "function" ? updater(prev) : updater;
      });
    },
    [userId, queryClient]
  );

  return {
    profile,
    loading,
    saving: mutation.isPending,
    updateProfile,
    setProfile,
  };
}
